import { Injectable } from '@angular/core';
import { FeatureFlagService } from './feature-flag.service';
import { ProductIdentifierKey } from '../models/product-identifier';

/**
 * Any payload that carries product identifiers (WB-7419).
 *
 * Deliberately structural rather than a union of concrete interfaces: the same
 * code is rendered from order lines (`IParseProduct`, `IQuotationOrderLine`),
 * products and variants (`IProduct`, `IVariant`), catalog rows, and a handful of
 * older payloads that prefix the fields with `product_` (force-send conflict
 * panel, kill-stock, products-availability dialog). One shape covers all of them
 * without forcing those models to converge.
 */
export interface ProductCodeCarrier {
  sku?: string | null;
  erp_id?: string | null;
  /** EAN/GTIN. Numeric on some payloads, string on others. */
  barcode?: string | number | null;
  /** Legacy aliases used by the conflict / kill-stock / availability payloads. */
  product_sku?: string | null;
  product_erp_id?: string | null;
}

/** i18n keys for the identifier labels. See the `product_code` namespace. */
const LABEL_KEYS: Record<ProductIdentifierKey, string> = {
  sku: 'product_code.sku',
  erp_id: 'product_code.erp_id',
  ean: 'product_code.ean',
};

/** Order used when listing every identifier — primary-agnostic, stable. */
const ALL_IDENTIFIERS: readonly ProductIdentifierKey[] = ['sku', 'erp_id', 'ean'];

export const PRODUCT_CODE_PLACEHOLDER = '--';

/**
 * Resolves the single product code to display, per the tenant's configured
 * primary identifier (WB-7419).
 *
 * **This is the only place the decision is made.** Per-component conditionals
 * are the failure mode the ticket calls out as its main source of risk: a code
 * rendered from ~40 sites will drift if each one decides for itself.
 *
 * Resolution is a mandatory chain: configured primary -> SKU -> placeholder.
 * `erp_id` is not guaranteed populated (products created manually in Volta, or
 * not yet synced from the ERP), and a blank code where a code used to be is
 * worse than showing the SKU.
 */
@Injectable({ providedIn: 'root' })
export class ProductCodeService {
  constructor(private featureFlags: FeatureFlagService) {}

  /** Read a single identifier, normalising ''/'  '/null/undefined to null. */
  private read(
    product: ProductCodeCarrier | null | undefined,
    key: ProductIdentifierKey
  ): string | null {
    if (!product) {
      return null;
    }
    // `product_*` aliases are checked second so a payload carrying both agrees
    // with the canonical field.
    const raw =
      key === 'sku'
        ? product.sku ?? product.product_sku
        : key === 'erp_id'
        ? product.erp_id ?? product.product_erp_id
        : product.barcode;

    const value = raw === null || raw === undefined ? '' : String(raw).trim();
    return value === '' ? null : value;
  }

  /**
   * Which identifier actually supplied the displayed value — the configured
   * primary, or `'sku'` when the primary was empty and the fallback kicked in.
   * `null` when the product carries no usable code at all.
   */
  resolvedKey(
    product: ProductCodeCarrier | null | undefined
  ): ProductIdentifierKey | null {
    const primary = this.featureFlags.primaryProductIdentifier;
    if (this.read(product, primary) !== null) {
      return primary;
    }
    return this.read(product, 'sku') !== null ? 'sku' : null;
  }

  /**
   * The code to display: configured primary, falling back to SKU, falling back
   * to `placeholder`. Never returns an empty string.
   */
  resolve(
    product: ProductCodeCarrier | null | undefined,
    placeholder: string = PRODUCT_CODE_PLACEHOLDER
  ): string {
    const key = this.resolvedKey(product);
    return key === null ? placeholder : (this.read(product, key) as string);
  }

  /**
   * i18n key describing the value `resolve()` returned for *this* product — use
   * for per-row copy labels and tooltips, where the label should tell the truth
   * about the value next to it even when the SKU fallback was used.
   */
  labelKey(product: ProductCodeCarrier | null | undefined): string {
    return LABEL_KEYS[this.resolvedKey(product) ?? 'sku'];
  }

  /**
   * i18n key derived from the tenant config alone — use for **column headers**,
   * which are rendered once and cannot vary per row. Using `labelKey()` here
   * would let a single fallback row rename the whole column.
   */
  get primaryLabelKey(): string {
    return LABEL_KEYS[this.featureFlags.primaryProductIdentifier];
  }

  /** True when this tenant displays something other than the SKU. */
  get isCustomPrimary(): boolean {
    return this.featureFlags.primaryProductIdentifier !== 'sku';
  }
}
